import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Image, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { Inventory2Outlined, LocalShippingOutlined, CheckCircleOutline, CancelOutlined, RateReviewOutlined } from '@mui/icons-material';
import moment from 'moment';

import { updateOrderStatus } from '../services/OrderServices';
import ReportModal from './ReportModal';
import ReviewModal from './ReviewModal';
import { formatPrice } from './product/productUtils';

export const orderState = (o) => o.cancel ? 'cancelled' : o.delivered ? 'delivered' : o.onWay ? 'onWay' : 'preparing';

const STATES = {
    preparing: { label: 'Preparing', icon: Inventory2Outlined, color: '#9A5B13', bg: '#FFF4E5' },
    onWay: { label: 'On the way', icon: LocalShippingOutlined, color: '#1F4E79', bg: '#E6F1FA' },
    delivered: { label: 'Delivered', icon: CheckCircleOutline, color: '#1E6B3A', bg: '#E8F5EC' },
    cancelled: { label: 'Cancelled', icon: CancelOutlined, color: '#8B0000', bg: '#FBEAEA' },
};

// One order on "My orders": status, the items bought (from the order's saved snapshot), total, actions
const OrderCard = ({ order: initial }) => {

    const toast = useToast();
    const report = useDisclosure();
    const [order, setOrder] = useState(initial);
    const [reviewing, setReviewing] = useState(null);
    const state = orderState(order);
    const S = STATES[state];
    const items = order.items?.length ? order.items : [];

    const cancel = () => {
        updateOrderStatus(order._id, false, false, false, false, true)
            .then((result) => {
                if (result.status === 'failed') throw new Error(result.error);
                setOrder({ ...order, status: false, prepare: false, cancel: true });
                toast({ title: 'Order cancelled', status: 'success', duration: 2000, position: 'top' });
            })
            .catch(() => toast({ title: 'Could not cancel this order', status: 'error', duration: 2500, position: 'top' }));
    };

    return (
        <Box border='1px solid' borderColor='raano.border' borderRadius='8px' p={{ base: '14px', md: '20px' }} mb='14px' >
            <Box display='flex' justifyContent='space-between' alignItems='flex-start' gap='12px' >
                <Box>
                    <Text fontSize='15px' fontWeight={600} >Order #{order._id.slice(-6).toUpperCase()}</Text>
                    <Text fontSize='13px' color='raano.muted' >{moment(order.orderDate).format('D MMM YYYY, h:mm a')}</Text>
                </Box>
                <Box display='inline-flex' alignItems='center' gap='6px' px='10px' py='4px' borderRadius='full' bg={S.bg} color={S.color} fontSize='13px' fontWeight={600} flexShrink={0} >
                    <S.icon sx={{ fontSize: 16 }} />{S.label}
                </Box>
            </Box>

            <Box mt='12px' >
                {items.map((it, i) => (
                    <Box key={i} display='flex' gap='12px' py='10px' borderTop={i ? '1px solid' : 'none'} borderColor='raano.border' alignItems='center' >
                        <Box as={RouterLink} to={`/product/${it.product}`} width='56px' flexShrink={0} bg='raano.imageBg' sx={{ aspectRatio: '2 / 3' }} >
                            {it.imageUrl && <Image src={it.imageUrl} alt={it.name} width='100%' height='100%' objectFit='cover' />}
                        </Box>
                        <Box flex={1} minWidth={0} >
                            <Text fontSize='14px' fontWeight={500} noOfLines={1} >{it.name}</Text>
                            <Text fontSize='12px' color='raano.muted' >{it.size ? `Size ${it.size} · ` : ''}Qty {it.quantity} × {formatPrice(it.price, { space: false })}</Text>
                        </Box>
                        {state === 'delivered' &&
                            <Box as='button' display='inline-flex' alignItems='center' gap='4px' fontSize='13px' textDecoration='underline' flexShrink={0} onClick={() => setReviewing(it.product)} >
                                <RateReviewOutlined sx={{ fontSize: 16 }} />Review
                            </Box>}
                    </Box>
                ))}
                {!items.length && <Text fontSize='13px' color='raano.muted' >{(order.products || []).length} item(s)</Text>}
            </Box>

            <Box display='flex' justifyContent='space-between' alignItems='center' gap='12px' mt='12px' pt='12px' borderTop='1px solid' borderColor='raano.border' flexWrap='wrap' >
                <Text fontSize='15px' fontWeight={700} >Total {formatPrice(order.total || 0, { space: false })}</Text>
                <Box display='flex' gap='8px' >
                    {state === 'preparing' && <Button variant='wipe' height='36px' px='16px' fontSize='13px' onClick={cancel} >Cancel order</Button>}
                    {state === 'delivered' && <Button variant='wipe' height='36px' px='16px' fontSize='13px' onClick={report.onOpen} >Report a problem</Button>}
                </Box>
            </Box>

            <ReportModal onClose={report.onClose} isOpen={report.isOpen} orderId={order._id} />
            <ReviewModal isOpen={!!reviewing} onClose={() => setReviewing(null)} productId={reviewing} />
        </Box>
    );
};

export default OrderCard;
